import React from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { parseISO, format, subDays, startOfDay, endOfDay, startOfMonth,  isSameDay } from 'date-fns';
import { BookingAnalyticsResponse, getBookingAnalytics } from "../../../lib/apis/analytics";
import { getRouteSegment } from "../../../lib/utils/funcs";
import { useUserData } from "../../../lib/contexts/UserContext";
import { FiChevronDown } from 'react-icons/fi';
import CompactTotalAppointmentsCard from '../../molecules/CompactTotalAppointmentsCard';
import BookingAnalyticsCard from '../../molecules/BookingAnalyticsCard';

type MetricKey =
  | "Dashboard Appointments"
  | "Whatsapp Appointments"
  | "Walk-in Bookings"
  | "Follow-up Bookings";

// Constants
const BOOKING_METRICS: MetricKey[] = [
  "Dashboard Appointments",
  "Whatsapp Appointments", 
  "Walk-in Bookings",
  "Follow-up Bookings"
];

interface CachedData {
  data: BookingAnalyticsResponse;
  timestamp: string;
}

// Add props interface
interface BookingAnalyticsGraphProps {
  hospitalId?: string;
  startDate?: string;
  endDate?: string;
  report?:boolean;
  bookingStats?:any;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
}

export default function BookingAnalyticsGraph({ hospitalId, startDate, endDate, report, bookingStats, onDateRangeChange }: 
  BookingAnalyticsGraphProps) {
  const { userData } = useUserData();
  // Use prop if provided, else fallback to route segment
  const hospital_id = hospitalId || getRouteSegment(1);
  const [selectedRange, setSelectedRange] = React.useState("7 days");
  const [selectedMetrics, setSelectedMetrics] = React.useState<MetricKey[]>(BOOKING_METRICS);
  const [showDateRangeModal, setShowDateRangeModal] = React.useState(false);
  const [modalSelectedRange, setModalSelectedRange] = React.useState(selectedRange);
  const [customStartDate, setCustomStartDate] = React.useState(startDate || format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [customEndDate, setCustomEndDate] = React.useState(endDate || format(startOfDay(new Date()), 'yyyy-MM-dd'));
  const [analyticsData, setAnalyticsData] = React.useState<BookingAnalyticsResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [lastUpdateTime, setLastUpdateTime] = React.useState<string | null>(null);

  // Add a flag to determine if using external (PDF/chart-only) mode
  const isExternal = !!(hospitalId && startDate && endDate);

  // Function to check if cached data is from a different day
  const isCacheFromDifferentDay = React.useCallback((timestamp: string) => {
    const cacheDate = new Date(timestamp);
    const currentDate = new Date();
    return !isSameDay(cacheDate, currentDate);
  }, []);

  // Calculate date range based on selected range
  const getDateRange = React.useCallback((range: string) => {
    if (isExternal && startDate && endDate) {
      return { startDate, endDate };
    }
    const yesterday = subDays(new Date(), 1);
    const computedEndDate = format(endOfDay(yesterday), 'yyyy-MM-dd');
    
    switch (range) {
      case "7 days":
        const sevenDaysAgo = subDays(yesterday, 6);
        return { startDate: format(startOfDay(sevenDaysAgo), 'yyyy-MM-dd'), endDate: computedEndDate };
      case "28 days":
        const twentyEightDaysAgo = subDays(yesterday, 27);
        return { startDate: format(startOfDay(twentyEightDaysAgo), 'yyyy-MM-dd'), endDate: computedEndDate };
      case "3 months":
        const threeMonthsAgo = subDays(yesterday, 89);
        return { startDate: format(startOfDay(threeMonthsAgo), 'yyyy-MM-dd'), endDate: computedEndDate };
      case "6 months":
        const sixMonthsAgo = subDays(yesterday, 179);
        return { startDate: format(startOfDay(sixMonthsAgo), 'yyyy-MM-dd'), endDate: computedEndDate };
      case "12 months":
        const twelveMonthsAgo = subDays(yesterday, 364);
        return { startDate: format(startOfDay(twelveMonthsAgo), 'yyyy-MM-dd'), endDate: computedEndDate };
      default:
        // For custom range, ensure dates are within valid range
        const customStart = parseISO(customStartDate);
        const customEnd = parseISO(customEndDate);
        if (customStart > customEnd) {
          return { startDate: customEndDate, endDate: customStartDate };
        }
        return { startDate: customStartDate, endDate: customEndDate };
    }
  }, [customStartDate, customEndDate, isExternal, startDate, endDate]);

  // Function to get cache key based on hospital_id and date range
  const getCacheKey = React.useCallback((range: string) => {
    const { startDate, endDate } = getDateRange(range);
    return `booking_analytics_${hospital_id}_${startDate}_${endDate}`;
  }, [hospital_id, getDateRange]);

  // Fetch analytics data with caching
  const fetchAnalyticsData = React.useCallback(async () => {
    // Set up delayed loading
    const loadingTimeout = setTimeout(() => {
      setLoading(true);
    }, 500);
    
    try {
      setError(null);
      const { startDate, endDate } = getDateRange(selectedRange);

      if(bookingStats){
        setAnalyticsData(bookingStats);
        setIsInitialLoad(false);
        return;
      }
      
      // Skip cache for custom date range
      if (selectedRange === "Custom") {
        console.log('Fetching fresh data for custom range:', { startDate, endDate });
        const data = await getBookingAnalytics({
          hospital_id: hospital_id,
          start_date: startDate,
          end_date: endDate,
          doctor_id: userData?.doctor_id
        });
        setAnalyticsData(data);
        setLastUpdateTime(new Date().toISOString());
        setIsInitialLoad(false);
        clearTimeout(loadingTimeout);
        setLoading(false);
        return;
      }

      const cacheKey = getCacheKey(selectedRange);
      
      // Check cache first
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const { data, timestamp }: CachedData = JSON.parse(cachedData);
        if (!isCacheFromDifferentDay(timestamp)) {
          setAnalyticsData(data);
          setLastUpdateTime(timestamp);
          setIsInitialLoad(false);
          clearTimeout(loadingTimeout);
          setLoading(false);
          return;
        }
      }

      // If no valid cache or cache is from a different day, fetch new data
      console.log('Fetching fresh data for range:', { startDate, endDate, selectedRange });
      
      const data = await getBookingAnalytics({
        hospital_id: hospital_id,
        start_date: startDate,
        end_date: endDate,
        doctor_id:userData?.doctor_id
      });

      // Cache the new data
      const currentTime = new Date().toISOString();
      const cacheData: CachedData = {
        data,
        timestamp: currentTime
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));

      setAnalyticsData(data);
      setLastUpdateTime(currentTime);
      setIsInitialLoad(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
    } finally {
      clearTimeout(loadingTimeout); 
      setLoading(false);
    }
  }, [hospital_id, selectedRange, getDateRange, getCacheKey, isCacheFromDifferentDay, bookingStats, userData]);

  React.useEffect(() => {
    if(userData && !bookingStats){
      fetchAnalyticsData();
    }
  }, [hospital_id, selectedRange, userData, fetchAnalyticsData, bookingStats]);

  // Handle bookingStats prop
  React.useEffect(() => {
    if(bookingStats){
      // Validate the structure
      if (bookingStats.daily_data && Array.isArray(bookingStats.daily_data) && bookingStats.totals) {
        setAnalyticsData(bookingStats);
        setIsInitialLoad(false);
      } else {
        console.error('Invalid bookingStats structure:', bookingStats);
        setError('Invalid data structure received');
      }
    }
  }, [bookingStats]);

  // Call the callback whenever date range changes
  React.useEffect(() => {
    if (onDateRangeChange) {
      const { startDate, endDate } = getDateRange(selectedRange);
      onDateRangeChange(startDate, endDate);
    }
  }, [selectedRange, customStartDate, customEndDate, onDateRangeChange, getDateRange, hospital_id]);

  // Transform analytics data for chart
  const transformAnalyticsData = React.useCallback(() => {
    if (!analyticsData) return [];

    if (!analyticsData.daily_data || analyticsData.daily_data.length === 0) {
      return [];
    }

    const colorMap = {
      "Dashboard Appointments": "#008FFB", // Blue
      "Whatsapp Appointments": "#00E396", // Green
      "Walk-in Bookings": "#FEB019", // Orange
      "Follow-up Bookings": "#8B5CF6", // Violet
    };

    return selectedMetrics.map(metric => {
      return {
        name: metric,
        data: analyticsData.daily_data.map(d => d[metric] || 0),
        color: colorMap[metric as keyof typeof colorMap]
      };
    });
  }, [analyticsData, selectedMetrics]);

  // Get X-axis categories
  const getXAxisCategories = React.useCallback(() => {
    if (!analyticsData?.daily_data) return [];
    return analyticsData.daily_data.map(e => {
      const date = parseISO(e.date);
      // Check if the date string is in YYYY-MM format (monthly data)
      if (e.date.length === 7) {
        return format(date, 'MMMM yyyy');
      }
      return format(date, 'd MMMM yyyy');
    });
  }, [analyticsData]);

  // Chart options
  const chartOptions = React.useMemo((): ApexOptions => ({
    chart: {
      height: 350,
      type: "area",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easein',
        speed: 300,
        animateGradually: {
            enabled: true,
            delay: 50
        },
        dynamicAnimation: {
            enabled: true,
            speed: 250
        }
    }
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "solid",
    },
    title: {
      align: "left",
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
    markers: {
      size: 0,
      hover: {
        sizeOffset: 6,
      },
    },
    xaxis: {
      categories: getXAxisCategories(),
      labels: {
        show: true,
        style: {
          fontSize: "12px",
          colors: "#A0AEC0",
        },
        rotate: -45,
        rotateAlways: false,
      },
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        formatter: function (val: number) {
          return val.toFixed(0);
        },
        style: {
          fontSize: "12px",
          colors: "#A0AEC0",
        },
      },
    },
    grid: {
      borderColor: "#f1f1f1",
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (
          val: number,
          { seriesIndex }: { seriesIndex: number }
        ) {
          const metric = selectedMetrics[seriesIndex];
          if (metric === "Walk-in Bookings") {
            return val.toFixed(0);
          } else if (metric === "Follow-up Bookings") {
            return val.toFixed(0);
          } else {
            return val.toFixed(0);
          }
        },
      },
    },
  }), [getXAxisCategories, selectedMetrics]);

  const chartSeries = transformAnalyticsData();

  const handleMetricChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    setSelectedMetrics((prevMetrics) => {
      const newMetrics = checked
        ? [...prevMetrics, value as MetricKey]
        : prevMetrics.filter((metric) => metric !== value);
      return newMetrics;
    });
  };

  const handleRangeChange = (range: string) => {
    setSelectedRange(range);
    setModalSelectedRange(range);
    setShowDateRangeModal(false);
    fetchAnalyticsData();
  };

  const handleModalApply = () => {
    setSelectedRange(modalSelectedRange);
    setShowDateRangeModal(false);
    fetchAnalyticsData();
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomEndDate(e.target.value);
  };

  return (
    <div className={`bg-white p-4 rounded-lg shadow mt-6 ${report ? 'print:shadow-none print:mt-0' : ''}`}>
      {isInitialLoad ? (
        <div className="flex items-center justify-center h-[350px]">
          <p className="text-gray-500">Select a date range to view analytics</p>
        </div>
      ) : (
        <>
          {loading && (
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="p-3 rounded-lg border border-gray-200">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                ))}
              </div>
              <div className="h-[350px] bg-gray-200 rounded"></div>
            </div>
          )}
          {error && <div className="text-red-500 text-center py-4">{error}</div>}
          {!loading && !error && (
            <>
              <div className={`flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4 ${report ? "hidden" : ""}`}>
                <div className="grid grid-cols-2 md:flex md:flex-wrap md:justify-start gap-2">
                  {[ "7 days", "28 days", "3 months"].map((range) => (
                    <button
                      key={range}
                      className={`px-4 py-1 md:py-2 rounded-md border flex gap-1 items-center ${
                        selectedRange === range
                          ? "border-blue-600 text-white bg-blue-600"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => handleRangeChange(range)}
                    >
                      {range}
                    </button>
                  ))}
                  <button 
                    className="px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-transform transform hover:scale-105"
                    onClick={() => setShowDateRangeModal(true)}
                  >
                    More
                    <FiChevronDown className="w-4 h-4 ml-1" />
                  </button>
                </div>
                <CompactTotalAppointmentsCard
                  dashboardAppointments={analyticsData?.totals["Dashboard Appointments"] || 0}
                  whatsappAppointments={analyticsData?.totals["Whatsapp Appointments"] || 0}
                  walkinAppointments={analyticsData?.totals["Walk-in Bookings"] || 0}
                  followupAppointments={analyticsData?.totals["Follow-up Bookings"] || 0}
                />
              </div>

              <div className={`grid gap-4 mb-4 ${report ? 'grid-cols-4 print:gap-2 booking-analytics-cards' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
                {BOOKING_METRICS.map((metric) => (
                  <BookingAnalyticsCard
                    key={metric}
                    metric={metric}
                    value={analyticsData?.totals[metric] || 0}
                    isSelected={selectedMetrics.includes(metric)}
                    report={report}
                    onClick={() =>
                      handleMetricChange({
                        target: {
                          value: metric,
                          checked: !selectedMetrics.includes(metric),
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                ))}
              </div>

              <div className={`text-sm mb-2 ${
                selectedMetrics.includes("Whatsapp Appointments") ? "text-white" : "text-gray-600"
              }`}>
                {selectedMetrics.length > 0 ? selectedMetrics[0] : "Dashboard Appointments"}
              </div>
              <div id="chart">
                <ReactApexChart
                  key={lastUpdateTime}
                  options={chartOptions}
                  series={chartSeries}
                  height={350}
                />
              </div>
              {showDateRangeModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg shadow-lg w-full sm:w-auto md:w-96 max-w-lg mx-4">
                    <h2 className="text-xl font-semibold mb-4">Date range</h2>
                    <div className="border-b border-gray-200 mb-4 flex">
                      <button className="pb-2 text-blue-600 border-b-2 border-blue-600 text-sm font-medium">Filter</button>
          
                    </div>
                    <div className="space-y-4">
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="dateRange" 
                          value="6 months" 
                          checked={modalSelectedRange === "6 months"}
                          onChange={() => setModalSelectedRange("6 months")}
                          className="form-radio text-blue-600"
                        />
                        <span className="ml-2 text-gray-700">Last 6 months</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="dateRange" 
                          value="12 months" 
                          checked={modalSelectedRange === "12 months"}
                          onChange={() => setModalSelectedRange("12 months")}
                          className="form-radio text-blue-600"
                        />
                        <span className="ml-2 text-gray-700">Last 12 months</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="dateRange" 
                          value="Custom" 
                          checked={modalSelectedRange === "Custom"}
                          onChange={() => setModalSelectedRange("Custom")}
                          className="form-radio text-blue-600"
                        />
                        <span className="ml-2 text-gray-700">Custom</span>
                      </label>
                    </div>
                    {modalSelectedRange === "Custom" && (
                      <div className="flex justify-between items-center mt-4">
                        <div>
                          <p className="text-xs text-gray-500">Start date</p>
                          <input 
                            type="date" 
                            value={customStartDate}
                            max={format(startOfDay(new Date()), 'yyyy-MM-dd')}
                            onChange={handleStartDateChange}
                            className="border rounded-md px-2 py-1 mt-1 text-sm"
                          />
                        </div>
                        <span className="mx-2 text-gray-500 relative top-2">-</span>
                        <div>
                          <p className="text-xs text-gray-500">End date</p>
                          <input 
                            type="date" 
                            value={customEndDate}
                            min={customStartDate}
                            max={format(startOfDay(new Date()), 'yyyy-MM-dd')}
                            onChange={handleEndDateChange}
                            className="border rounded-md px-2 py-1 mt-1 text-sm"
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end space-x-2 mt-6">
                      <button 
                        className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowDateRangeModal(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                        onClick={handleModalApply}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}